import { useState, useContext } from "react"
import { Breadcrumb, TitleScreen, Modal } from "components/Core"
import { HiSearch, HiChevronRight, HiChevronDown, HiOutlineInformationCircle, HiCheck, HiOutlineClipboardCopy } from "react-icons/hi"
import { post_middleware_query } from "services/middleware_services"
import useCancelToken from "hooks/useCancelToken"
import AuthContext from "store/context/PublicContext"
import { Spinner } from "flowbite-react"


const OrderReinjectionScreen = () => {
  const [salesChannelId, setSalesChannelId] = useState("")
  const [orderInfo, setOrderInfo] = useState<any>(null)
  const [sgInfo, setSgInfo] = useState<any>(null)
  const [activeTab, setActiveTab] = useState<"SG" | "ORDER">("ORDER")
  const [viewMode, setViewMode] = useState<"LIST" | "JSON">("LIST")
  const [showNotFound, setShowNotFound] = useState(false)
  const [loading, setLoading] = useState(false)
  const { user: { token } } = useContext(AuthContext)
  const { newCancelToken } = useCancelToken()

  const handleSearch = async () => {
    if (!salesChannelId || !token) return
    
    setLoading(true)
    try {
      const orderPromise = post_middleware_query(
        { entityType: "Order", filter: { salesChannelId } },
        token,
        newCancelToken()
      )
      
      const sgPromise = post_middleware_query(
        { entityType: "ShippingGroup", filter: { salesChannelId } },
        token,
        newCancelToken()
      )

      const [orderRes, sgRes] = await Promise.all([orderPromise, sgPromise])
      
      const foundOrder = orderRes.data.message?.records?.[0] || null
      const foundSg = sgRes.data.message?.records?.[0] || null

      if (foundOrder || foundSg) {
        setOrderInfo(foundOrder)
        setSgInfo(foundSg)
        setShowNotFound(false)
        if (foundOrder) setActiveTab("ORDER")
        else setActiveTab("SG")
      } else {
        setOrderInfo(null)
        setSgInfo(null)
        setShowNotFound(true)
      }
    } catch (error) {
      console.error("Error searching order:", error)
      setOrderInfo(null)
      setSgInfo(null)
      setShowNotFound(true)
    } finally {
      setLoading(false)
    }
  }

  // iOS-style Collapsible Data Group
  const DataGroup = ({ title, children, defaultOpen = true }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen)
    
    return (
        <div className="w-full flex flex-col gap-1 mb-6">
            <button 
                onClick={() => setIsOpen(!isOpen)}
                className="flex flex-row items-center justify-between ml-4 pr-4 py-1 group transition-colors"
            >
                <h3 className="text-[13px] font-semibold text-gray-500 uppercase tracking-tight group-hover:text-gray-700">{title}</h3>
                {isOpen ? <HiChevronDown className="h-4 w-4 text-gray-400" /> : <HiChevronRight className="h-4 w-4 text-gray-400" />}
            </button>
            <div className={`w-full bg-white rounded-[10px] overflow-hidden border-y sm:border-x border-gray-200 shadow-sm transition-all duration-300 ${isOpen ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0 pointer-events-none border-none shadow-none'}`}>
                <div className="flex flex-col">
                {children}
                </div>
            </div>
        </div>
    )
  }

  // iOS-style Data Item (List row)
  const DataItem = ({ label, value, last }: { label: string; value: any; last?: boolean }) => {
    const isEmpty = value === null || value === undefined || value === "";
    return (
      <div className={`flex flex-row justify-between items-center py-3 px-4 ${!last ? 'border-b border-gray-100' : ''}`}>
        <p className="text-[15px] font-normal text-gray-800">{label}</p>
        <p className={`text-[15px] font-medium text-right max-w-[60%] break-all ${isEmpty ? 'text-gray-300 italic' : 'text-gray-500'}`}>
          {isEmpty ? "[Vacío]" : String(value)}
        </p>
      </div>
    )
  }

  const JsonView = ({ data }: { data: any }) => {
    const [copied, setCopied] = useState(false)

    const copyToClipboard = () => {
      navigator.clipboard.writeText(JSON.stringify(data, null, 2))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }

    return (
      <div className="w-full bg-[#1C1C1E] rounded-xl p-4 overflow-hidden shadow-inner border border-gray-800 relative group">
        <button 
          onClick={copyToClipboard}
          className="absolute top-3 right-3 p-2 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg text-gray-400 hover:text-white transition-all border border-gray-700/50 opacity-0 group-hover:opacity-100 flex items-center gap-1.5 backdrop-blur-sm"
        >
          {copied ? (
            <>
              <HiCheck className="h-4 w-4 text-green-400" />
              <span className="text-[11px] font-bold text-green-400">Copiado</span>
            </>
          ) : (
            <>
              <HiOutlineClipboardCopy className="h-4 w-4" />
              <span className="text-[11px] font-bold">Copiar JSON</span>
            </>
          )}
        </button>
        <div className="overflow-auto max-h-[1000px]">
          <pre className="text-[13px] font-mono text-[#D1D1D6] leading-relaxed">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full flex flex-col items-start justify-start bg-[#F2F2F7] font-sans overflow-auto select-none">
      <Breadcrumb
        data={[
          { name: "Pre-releases", href: "#" },
          { name: "reinyección de orden", href: "/pre-releases/order-reinjection" },
        ]}
      />
      <div className="w-full flex flex-col justify-start items-center py-6 px-4 gap-6 pb-20">
        <TitleScreen title="reinyección de orden" />

        {/* iOS-style Search Bar Container */}
        <div className="w-full max-w-2xl bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col gap-4">
          <div className="relative">
             <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <HiSearch className="h-5 w-5 text-gray-400" />
             </div>
             <input
               type="text"
               placeholder="Ej: 846495556"
               className="block w-full pl-10 pr-3 py-2.5 bg-[#EEEFF0] border-none rounded-lg text-sm focus:ring-[#007AFF] transition-all"
               value={salesChannelId}
               onChange={(e) => setSalesChannelId(e.target.value)}
               onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
             />
          </div>
            <button
              onClick={handleSearch}
              disabled={!salesChannelId || loading}
              className={`px-6 py-2 rounded-lg font-semibold transition-all flex items-center justify-center min-w-[120px] ${
                !salesChannelId || loading
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-[#007AFF] text-white hover:bg-[#0062CC] active:scale-95 shadow-sm"
              }`}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  <span>Buscando...</span>
                </>
              ) : (
                <>
                  <HiSearch className="h-5 w-5" />
                  Buscar Orden
                </>
              )}
            </button>
        </div>

        {(orderInfo || sgInfo) && (
          <div className="w-full max-w-2xl flex flex-col pt-2 slide-up-animation">
            {/* HEADER AREA (Now before tabs) */}
            <div className="flex justify-between items-center mb-6 px-4">
              <div className="flex flex-col">
                <p className="text-xs font-bold text-[#007AFF] uppercase tracking-widest">
                  {activeTab === "ORDER" ? "Detalle de Orden" : "Detalle Shipping Group"}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {activeTab === "ORDER" ? (orderInfo?.id || "N/A") : (sgInfo?.id || "N/A")}
                </p>
              </div>
              <button 
                onClick={() => alert("Verificando...")}
                className="bg-white border text-[#007AFF] border-[#007AFF] px-4 py-2 rounded-full text-sm font-semibold active:bg-[#007AFF] active:text-white transition-colors"
                >
                Verificar orden
              </button>
            </div>

            <div className="flex flex-col gap-3 mb-8">
              {/* iOS-style Segmented Control (Tabs) */}
              <div className="w-full bg-[#E3E3E8] p-1 rounded-[9px] flex flex-row shadow-inner">
                 <button 
                   onClick={() => setActiveTab("ORDER")}
                   className={`flex-1 py-1.5 rounded-[7px] text-[13px] font-semibold transition-all ${activeTab === "ORDER" ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                 >
                   Orden
                 </button>
                 <button 
                   onClick={() => setActiveTab("SG")}
                   className={`flex-1 py-1.5 rounded-[7px] text-[13px] font-semibold transition-all ${activeTab === "SG" ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                 >
                   Shipping Group
                 </button>
              </div>

              {/* View Mode Toggle */}
              <div className="flex flex-row justify-end px-2">
                <div className="bg-[#E3E3E8] p-0.5 rounded-[7px] flex flex-row w-fit">
                   <button 
                     onClick={() => setViewMode("LIST")}
                     className={`px-4 py-1 rounded-[6px] text-[11px] font-bold transition-all ${viewMode === "LIST" ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}
                   >
                     Lista
                   </button>
                   <button 
                     onClick={() => setViewMode("JSON")}
                     className={`px-4 py-1 rounded-[6px] text-[11px] font-bold transition-all ${viewMode === "JSON" ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}
                   >
                     JSON
                   </button>
                </div>
              </div>
            </div>

            {/* TAB CONTENT: ORDER */}
            {activeTab === "ORDER" && orderInfo && (
              viewMode === "LIST" ? (
                <div className="flex flex-col">
                  <DataGroup title="General Orden">
                    <DataItem label="id" value={orderInfo.id} />
                    <DataItem label="shippingType" value={orderInfo.shippingType} />
                    <DataItem label="estimatedDeliveryDate" value={orderInfo.estimatedDeliveryDate} />
                    <DataItem label="deliveryType" value={orderInfo.deliveryType} />
                    <DataItem label="channel" value={orderInfo.channel} />
                    <DataItem label="salesChannelId" value={orderInfo.salesChannelId} />
                    <DataItem label="orderType" value={orderInfo.orderType} />
                    <DataItem label="creationDate" value={orderInfo.creationDate} />
                    <DataItem label="systemId" value={orderInfo.systemId} />
                    <DataItem label="serviceRequest" value={orderInfo.serviceRequest} />
                    <DataItem label="distance" value={orderInfo.distance} last />
                  </DataGroup>

                  <DataGroup title="Slot Delivery">
                    <DataItem label="initialHour" value={orderInfo.slotDelivery?.initialHour} />
                    <DataItem label="finalHour" value={orderInfo.slotDelivery?.finalHour} />
                    <DataItem label="slotType" value={orderInfo.slotDelivery?.slotType} last />
                  </DataGroup>

                  <DataGroup title="Customer Details">
                    <DataItem label="customer.id" value={orderInfo.customer?.id} />
                    <DataItem label="customer.name" value={orderInfo.customer?.name} />
                    <DataItem label="customer.email" value={orderInfo.customer?.email} />
                    <DataItem label="customer.phone" value={orderInfo.customer?.phone} />
                    <DataItem label="customer.phone2" value={orderInfo.customer?.phone2} />
                    <DataItem label="customer.type" value={orderInfo.customer?.type} />
                    <DataItem label="customer.contactType" value={orderInfo.customer?.contactType} last />
                  </DataGroup>

                  <DataGroup title="Shipping Address">
                    <DataItem label="country" value={orderInfo.shippingAddress?.country} />
                    <DataItem label="region" value={orderInfo.shippingAddress?.region} />
                    <DataItem label="province" value={orderInfo.shippingAddress?.province} />
                    <DataItem label="commune" value={orderInfo.shippingAddress?.commune} />
                    <DataItem label="zipCode" value={orderInfo.shippingAddress?.zipCode} />
                    <DataItem label="addressName" value={orderInfo.shippingAddress?.addressName} />
                    <DataItem label="addressNumber" value={orderInfo.shippingAddress?.addressNumber} />
                    <DataItem label="contact.name" value={orderInfo.shippingAddress?.contact?.name} />
                    <DataItem label="contact.phone" value={orderInfo.shippingAddress?.contact?.phone} />
                    <DataItem label="geo.latitude" value={orderInfo.shippingAddress?.georeference?.latitude} />
                    <DataItem label="geo.longitude" value={orderInfo.shippingAddress?.georeference?.longitude} last />
                  </DataGroup>

                  <DataGroup title="Billing Address">
                    <DataItem label="country" value={orderInfo.billingAddress?.country} />
                    <DataItem label="region" value={orderInfo.billingAddress?.region} />
                    <DataItem label="province" value={orderInfo.billingAddress?.province} />
                    <DataItem label="commune" value={orderInfo.billingAddress?.commune} />
                    <DataItem label="zipCode" value={orderInfo.billingAddress?.zipCode} />
                    <DataItem label="addressName" value={orderInfo.billingAddress?.addressName} />
                    <DataItem label="addressNumber" value={orderInfo.billingAddress?.addressNumber} />
                    <DataItem label="contact.name" value={orderInfo.billingAddress?.contact?.name} />
                    <DataItem label="contact.phone" value={orderInfo.billingAddress?.contact?.phone} />
                    <DataItem label="geo.latitude" value={orderInfo.billingAddress?.georeference?.latitude} />
                    <DataItem label="geo.longitude" value={orderInfo.billingAddress?.georeference?.longitude} last />
                  </DataGroup>

                  <DataGroup title="Items">
                    {orderInfo.items?.map((item: any, idx: number) => (
                      <div key={idx} className="bg-gray-50/30 p-2 m-2 border rounded-lg">
                        <p className="px-4 py-1 text-xs font-bold text-gray-400">ITEM {idx + 1}</p>
                        <DataItem label="name" value={item.name} />
                        <DataItem label="sku" value={item.sku} />
                        <DataItem label="quantity" value={item.quantity} />
                        <DataItem label="type" value={item.type} />
                        <DataItem label="volume" value={item.volume} last />
                      </div>
                    ))}
                  </DataGroup>

                  <DataGroup title="Payment Info" defaultOpen={false}>
                    {orderInfo.paymentInfo?.map((pay: any, idx: number) => (
                      <div key={idx} className="bg-gray-50/80 p-2 m-2 border rounded-lg">
                        <p className="px-4 py-1 text-xs font-bold text-gray-400">PAYMENT {idx + 1}</p>
                        <DataItem label="requestId" value={pay.requestId} />
                        <DataItem label="@source" value={pay["@source"]} />
                        <DataItem label="transacationType" value={pay.transacationType} />
                        <DataItem label="transationNumber" value={pay.transationNumber} />
                        <DataItem label="customerOrderId" value={pay.customerOrderId} />
                        <DataItem label="transactionDate" value={pay.transactionDate} />
                        <DataItem label="sellerId" value={pay.sellerId} />
                        <DataItem label="subtotalAmount" value={pay.subtotalAmount} />
                        <DataItem label="taxAmount" value={pay.taxAmount} />
                        <DataItem label="totalAmount" value={pay.totalAmount} />
                        <DataItem label="balanceDue" value={pay.balanceDue} />
                        <DataItem label="additionalCharges" value={pay.additionalCharges} />
                        <DataItem label="money.unit" value={pay.money?.unit} />
                        <DataItem label="underReviewFlag" value={pay.underReviewFlag} />
                        
                        <div className="bg-white m-2 border rounded-lg">
                          <p className="px-4 py-1 text-xs font-bold text-[#007AFF]">ACCOUNT</p>
                          <DataItem label="acc.id" value={pay.account?.id} />
                          <DataItem label="acc.firstName" value={pay.account?.firstName} />
                          <DataItem label="acc.lastName" value={pay.account?.lastName} />
                          <DataItem label="acc.orgName" value={pay.account?.organizationName} last />
                        </div>

                        <div className="bg-white m-2 border rounded-lg">
                          <p className="px-4 py-1 text-xs font-bold text-[#007AFF]">ADDRESS (Payment)</p>
                          <DataItem label="addr.streetFull" value={pay.address?.streetFullname} />
                          <DataItem label="addr.city" value={pay.address?.city} />
                          <DataItem label="addr.commune" value={pay.address?.commune} />
                          <DataItem label="addr.zipCode" value={pay.address?.zipCode} last />
                        </div>
                      </div>
                    ))}
                  </DataGroup>

                  <DataGroup title="Omnix Metadata" defaultOpen={false}>
                    <DataItem label="id" value={orderInfo._omnixMetadata?.id} />
                    <DataItem label="createdAt" value={orderInfo._omnixMetadata?.createdAt} />
                    <DataItem label="updatedAt" value={orderInfo._omnixMetadata?.updatedAt} />
                    <DataItem label="entityVersion" value={orderInfo._omnixMetadata?.entityVersion} />
                    <DataItem label="ownerId" value={orderInfo._omnixMetadata?.ownerId} last />
                  </DataGroup>

                  <DataGroup title="Timeline Events">
                     {orderInfo._omnixStateMachine?.states?.map((s: any, idx: number) => (
                        <DataItem key={idx} label={s.state?.name} value={s.date} last={idx === orderInfo._omnixStateMachine.states.length - 1} />
                     ))}
                  </DataGroup>

                  <DataGroup title="Technical Extra" defaultOpen={false}>
                     <DataItem label="confirmDispatchData.callAmount" value={sgInfo?.confirmDispatchData?.callAmount} />
                     <DataItem label="distance" value={orderInfo.distance} />
                     <DataItem label="newCoords.lat" value={orderInfo.newCoordinates?.latitude} />
                     <DataItem label="newCoords.long" value={orderInfo.newCoordinates?.longitude} last />
                  </DataGroup>
                </div>
              ) : (
                <JsonView data={orderInfo} />
              )
            )}

            {/* TAB CONTENT: SHIPPING GROUP */}
            {activeTab === "SG" && sgInfo && (
              viewMode === "LIST" ? (
                <div className="flex flex-col">
                  <DataGroup title="General SG">
                    <DataItem label="id" value={sgInfo.id} />
                    <DataItem label="orderId" value={sgInfo.orderId} />
                    <DataItem label="salesChannelId" value={sgInfo.salesChannelId} />
                    <DataItem label="channel" value={sgInfo.channel} />
                    <DataItem label="orderCreationDate" value={sgInfo.orderCreationDate} />
                    <DataItem label="shippingType" value={sgInfo.shippingType} />
                    <DataItem label="estimatedDeliveryDate" value={sgInfo.estimatedDeliveryDate} />
                    <DataItem label="distance" value={sgInfo.distance} />
                    <DataItem label="deliveryType" value={sgInfo.deliveryType} />
                    <DataItem label="orderType" value={sgInfo.orderType} />
                    <DataItem label="serviceRequest" value={sgInfo.serviceRequest} />
                    <DataItem label="unifiedSlotHour" value={sgInfo.unifiedSlotHour} />
                    <DataItem label="pickingId" value={sgInfo.pickingId} last />
                  </DataGroup>

                  <DataGroup title="Target (Customer Ref)">
                     <DataItem label="target.id" value={sgInfo.target?.id} />
                     <DataItem label="target.name" value={sgInfo.target?.name} />
                     <DataItem label="target.email" value={sgInfo.target?.email} />
                     <DataItem label="target.phone" value={sgInfo.target?.phone} />
                     <DataItem label="addr.addressName" value={sgInfo.target?.address?.addressName} />
                     <DataItem label="addr.addressNumber" value={sgInfo.target?.address?.addressNumber} />
                     <DataItem label="addr.commune" value={sgInfo.target?.address?.commune} last />
                  </DataGroup>

                  <DataGroup title="Source Details">
                     <DataItem label="source.id" value={sgInfo.source?.id} />
                     <DataItem label="source.name" value={sgInfo.source?.name} last />
                  </DataGroup>

                  <DataGroup title="Logistic Plan Items">
                     {sgInfo.logisticPlan?.items?.map((item: any, idx: number) => (
                        <div key={idx} className="bg-gray-50/30 p-2 m-2 border rounded-lg">
                          <p className="px-4 py-1 text-xs font-bold text-gray-400">PLAN ITEM {idx + 1}</p>
                          <DataItem label="sku" value={item.sku} />
                          <DataItem label="quantity" value={item.quantity} />
                          <DataItem label="type" value={item.type} />
                          <DataItem label="volume" value={item.volume} />
                          <DataItem label="cost" value={item.cost} />
                          {item.series?.map((s: any, sIdx: number) => (
                             <div key={sIdx} className="bg-white m-2 border rounded-md">
                                <DataItem label={`serie [${sIdx}]`} value={s.serie} />
                                <DataItem label="state" value={s.state} />
                                <DataItem label="valid_serie" value={s.valid_serie} last />
                             </div>
                          ))}
                        </div>
                     ))}
                  </DataGroup>

                  <DataGroup title="DTE Data">
                    <DataItem label="dte.category" value={sgInfo.dte?.category} />
                    <DataItem label="dte.expiresAt" value={sgInfo.dte?.expiresAt} />
                    <DataItem label="dte.fileName" value={sgInfo.dte?.fileName} />
                    <DataItem label="dte.folio" value={sgInfo.dte?.folio} />
                    <DataItem label="dte.url" value={sgInfo.dte?.url} last />
                  </DataGroup>

                  <DataGroup title="Timeline States (Detailed)">
                     {sgInfo._omnixStateMachine?.states?.map((s: any, idx: number) => (
                        <div key={idx} className="bg-white p-1 border-b last:border-0 border-gray-100">
                           <DataItem label={s.state?.name} value={s.date} />
                           {s.state?.custom?.executer && (
                             <p className="px-4 text-[11px] text-[#007AFF] font-bold">Exec: {s.state.custom.executer.name}</p>
                           )}
                        </div>
                     ))}
                  </DataGroup>

                  <DataGroup title="Custom Flags" defaultOpen={false}>
                     <DataItem label="closedOrder" value={sgInfo.custom?.closedOrder} />
                     <DataItem label="executerClosedOrder" value={sgInfo.custom?.executerClosedOrder} />
                     <DataItem label="withoutDocsDTE" value={sgInfo.withoutDocsDTE} last />
                  </DataGroup>

                  <DataGroup title="Dispatch Call History" defaultOpen={false}>
                    <DataItem label="eventId" value={sgInfo.confirmDispatchApigeeEventId} />
                    {sgInfo.confirmDispatchData?.history?.map((h: any, idx: number) => (
                       <div key={idx} className="bg-gray-50/20 p-2 border-t">
                          <DataItem label="response" value={h.input?.response} />
                          <DataItem label="channel" value={h.input?.channel} />
                          <DataItem label="date" value={h.createdAt} last />
                       </div>
                    ))}
                  </DataGroup>

                  <DataGroup title="Omnix Metadata (SG)" defaultOpen={false}>
                    <DataItem label="id" value={sgInfo._omnixMetadata?.id} />
                    <DataItem label="createdAt" value={sgInfo._omnixMetadata?.createdAt} />
                    <DataItem label="updatedAt" value={sgInfo._omnixMetadata?.updatedAt} />
                    <DataItem label="entityVersion" value={sgInfo._omnixMetadata?.entityVersion} last />
                  </DataGroup>
                </div>
              ) : (
                <JsonView data={sgInfo} />
              )
            )}
          </div>
        )}

        {/* Not Found Modal */}
        <Modal mounted={showNotFound} className="p-0 !bg-transparent">
            <div className="w-[320px] bg-white rounded-[14px] flex flex-col items-center overflow-hidden shadow-2xl">
                <div className="p-6 flex flex-col items-center gap-3 text-center">
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                        <HiOutlineInformationCircle className="h-8 w-8 text-red-500" />
                    </div>
                    <div className="flex flex-col gap-1">
                        <h4 className="text-[17px] font-bold text-gray-900">Orden no encontrada</h4>
                        <p className="text-[13px] text-gray-500 leading-tight">No pudimos localizar ninguna orden con el ID <span className="font-bold text-gray-800">{salesChannelId}</span>. Por favor verifique el número e intente nuevamente.</p>
                    </div>
                </div>
                <button 
                  onClick={() => setShowNotFound(false)}
                  className="w-full py-3 border-t border-gray-100 text-[#007AFF] font-bold text-[17px] active:bg-gray-50 transition-colors"
                >
                    Entendido
                </button>
            </div>
        </Modal>
      </div>

      <style>{`
        .slide-up-animation {
          animation: slideUp 0.4s ease-out;
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  )
}

export default OrderReinjectionScreen
